import { useEffect, useState } from 'react';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import {
  isActivityCategoryFilter,
  isActivityEventType,
  type ActivityCategory,
  type PatientActivityItem,
  type PatientActivityPayload,
} from '@/lib/patientActivity';

export const PATIENT_ACTIVITY_PAGE_SIZE = 40;

/** Pause before a new search hits the query key, so typing does not refetch per character. */
export const PATIENT_ACTIVITY_SEARCH_DEBOUNCE_MS = 300;

export function useDebouncedActivitySearch(search: string): string {
  const [debounced, setDebounced] = useState(search);
  useEffect(() => {
    const handle = window.setTimeout(
      () => setDebounced(search),
      PATIENT_ACTIVITY_SEARCH_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(handle);
  }, [search]);
  return debounced;
}

export const patientActivityQueryKey = (
  clinicId: string | null | undefined,
  patientId: string | null | undefined,
  filters: { category: ActivityCategory; search: string; from?: string; to?: string },
) =>
  [
    'patient-activity',
    clinicId,
    patientId,
    filters.category,
    filters.search,
    filters.from ?? null,
    filters.to ?? null,
  ] as const;

function asPayload(value: unknown): PatientActivityPayload {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return value as PatientActivityPayload;
}

function mapRow(row: {
  id: string;
  occurred_at: string;
  category: string;
  event_type: string;
  clinic_id: string;
  appointment_id: string | null;
  journal_entry_id: string | null;
  invoice_id: string | null;
  payment_id: string | null;
  email_id: string | null;
  sms_id: string | null;
  questionnaire_id: string | null;
  punch_card_id: string | null;
  payload: unknown;
}): PatientActivityItem | null {
  if (!isActivityEventType(row.event_type) || !isActivityCategoryFilter(row.category)) {
    return null;
  }
  return {
    id: row.id,
    occurred_at: row.occurred_at,
    category: row.category,
    event_type: row.event_type,
    clinic_id: row.clinic_id,
    appointment_id: row.appointment_id ?? null,
    journal_entry_id: row.journal_entry_id ?? null,
    invoice_id: row.invoice_id ?? null,
    payment_id: row.payment_id ?? null,
    email_id: row.email_id ?? null,
    sms_id: row.sms_id ?? null,
    questionnaire_id: row.questionnaire_id ?? null,
    punch_card_id: row.punch_card_id ?? null,
    payload: asPayload(row.payload),
  };
}

export function usePatientActivity(
  clinicId: string | null | undefined,
  patientId: string | null | undefined,
  filters: { category: ActivityCategory; search: string; from?: string; to?: string },
) {
  return useInfiniteQuery({
    queryKey: patientActivityQueryKey(clinicId, patientId, filters),
    // Previous rows stay visible while a new search or filter loads. Without this,
    // isLoading flips true, and PatientTabFrame unmounts the toolbar (search field included).
    placeholderData: keepPreviousData,
    enabled: Boolean(clinicId) && Boolean(patientId),
    initialPageParam: null as { occurred_at: string; id: string } | null,
    queryFn: async ({ pageParam }): Promise<PatientActivityItem[]> => {
      if (!clinicId || !patientId) return [];
      const { data, error } = await supabase.rpc('get_patient_activity', {
        p_clinic_id: clinicId,
        p_patient_id: patientId,
        p_category: filters.category,
        p_search: filters.search.trim() || null,
        p_from: filters.from ?? null,
        p_to: filters.to ?? null,
        p_limit: PATIENT_ACTIVITY_PAGE_SIZE,
        p_before: pageParam?.occurred_at ?? null,
        p_before_id: pageParam?.id ?? null,
      });
      if (error) throw error;
      return (data ?? [])
        .map((row) => mapRow(row))
        .filter((row): row is PatientActivityItem => row !== null);
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.length < PATIENT_ACTIVITY_PAGE_SIZE) return undefined;
      const last = lastPage[lastPage.length - 1];
      if (!last) return undefined;
      return { occurred_at: last.occurred_at, id: last.id };
    },
    staleTime: 15_000,
  });
}
